import React, { useState } from 'react';
import { CreditCard, Lock, AlertCircle, CheckCircle, Loader2, ExternalLink } from 'lucide-react';

interface MercadoPagoCheckoutSimpleProps {
  amount: number;
  description: string;
  userEmail: string;
  userId: string;
  planId: string;
  planName: string;
  onSuccess: (result: any) => void;
  onError: (error: string) => void;
}

const MercadoPagoCheckoutSimple: React.FC<MercadoPagoCheckoutSimpleProps> = ({
  amount,
  description,
  userEmail,
  userId,
  planId,
  planName,
  onSuccess,
  onError
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleRedirectCheckout = async () => {
    setIsLoading(true);
    try {
      // Criar preferência de pagamento
      const response = await fetch('https://api.mercadopago.com/checkout/preferences', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer APP_USR-8521871442707684-091022-2eec4e441795729d611857fffb6a22b8-39045970`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              title: description,
              quantity: 1,
              unit_price: amount,
              currency_id: 'BRL'
            }
          ],
          payer: {
            email: userEmail
          },
          back_urls: {
            success: `${window.location.origin}/pagamento/sucesso`,
            failure: `${window.location.origin}/pagamento/erro`,
            pending: `${window.location.origin}/pagamento/pendente`
          },
          auto_return: 'approved',
          metadata: {
            user_id: userId,
            plan_id: planId,
            plan_name: planName
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Erro na API do Mercado Pago: ${response.status}`);
      }

      const preference = await response.json();
      
      // Redirecionar para o checkout do Mercado Pago
      const checkoutUrl = `https://sandbox.mercadopago.com.br/checkout/v1/redirect?pref_id=${preference.id}`;
      window.location.href = checkoutUrl;
      
    } catch (error) {
      onError('Erro ao criar checkout: ' + (error as Error).message);
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="text-center mb-6">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Finalizar Pagamento
        </h3>
        <p className="text-gray-600">
          Você será redirecionado para o Mercado Pago
        </p>
      </div>

      {/* Resumo do Pedido */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-900 mb-3">Resumo do Pedido</h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Plano:</span>
            <span className="font-medium">{planName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Valor:</span>
            <span className="font-medium">R$ {amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Email:</span>
            <span className="font-medium">{userEmail}</span>
          </div>
        </div>
      </div>

      {/* Botão de Pagamento */}
      <button
        onClick={handleRedirectCheckout}
        disabled={isLoading}
        className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processando...
          </>
        ) : (
          <>
            <ExternalLink className="w-5 h-5" />
            Pagar com Mercado Pago
          </>
        )}
      </button>

      {/* Informações de Segurança */}
      <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-medium text-green-800 mb-1">Pagamento Seguro</h5>
            <p className="text-sm text-green-700">
              Seus dados são protegidos pelo Mercado Pago. Não armazenamos informações de cartão.
            </p>
          </div>
        </div>
      </div>

      {/* Informações sobre o Processo */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-medium text-blue-800 mb-1">Como Funciona</h5>
            <p className="text-sm text-blue-700">
              1. Clique em "Pagar com Mercado Pago"<br/>
              2. Complete o pagamento na página segura<br/>
              3. Retorne automaticamente para ativar sua conta
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MercadoPagoCheckoutSimple;
